import { useCallback, useEffect, useState } from 'react';
import type { SortDir } from '@vidmakr/shared';

/**
 * Per-browser view settings of the project browser and the asset library
 * (grid or table, sort column, inspector, sidebar folders). Storage can be
 * unavailable (private mode, blocked cookies): then they last for the visit.
 */
export function readStored<T>(key: string, fallback: T, valid?: (v: unknown) => boolean): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const v = JSON.parse(raw) as unknown;
    return !valid || valid(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeStored(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not remembered; it still applies until the page reloads.
  }
}

/** useState that remembers its value in localStorage under `key`. */
export function useStoredState<T>(key: string, fallback: T, valid?: (v: unknown) => boolean) {
  const [value, setValue] = useState<T>(() => readStored(key, fallback, valid));
  const set = useCallback(
    (next: T | ((prev: T) => T)) =>
      setValue((prev) => {
        const v = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        writeStored(key, v);
        return v;
      }),
    [key],
  );
  return [value, set] as const;
}

export type ViewMode = 'table' | 'grid';

/** Table (the default) or grid, remembered per page. */
export function useViewMode(page: 'projects' | 'assets') {
  return useStoredState<ViewMode>(`vm:view:${page}`, 'table', (v) => v === 'table' || v === 'grid');
}

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/** The sort column of a page's table, remembered per page; clicking the active column flips its direction. */
export function useSort<K extends string>(page: 'projects' | 'assets' | 'exports', keys: readonly K[], fallback: SortState<K>) {
  const [sort, setSort] = useStoredState<SortState<K>>(
    `vm:sort:${page}`,
    fallback,
    (v) => !!v && typeof v === 'object' && keys.includes((v as SortState<K>).key) && ['asc', 'desc'].includes((v as SortState<K>).dir),
  );
  /** Text columns start A→Z, the others (sizes, dates) largest/newest first. */
  const toggle = (key: K, firstDir: SortDir) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: firstDir }));
  return [sort, toggle] as const;
}

const WIDE = '(min-width: 1280px)';

/**
 * The inspector on the right of the project browser and asset library. On
 * wide screens it sits next to the content and is remembered open or closed;
 * on narrower ones it slides over the page and starts closed.
 */
export function useInspector() {
  const wideNow = () => typeof window !== 'undefined' && !!window.matchMedia?.(WIDE).matches;
  const [wide, setWide] = useState(wideNow);
  const [stored, setStored] = useStoredState('vm:inspector', true, (v) => typeof v === 'boolean');
  const [overlay, setOverlay] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.(WIDE);
    if (!mq) return;
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const open = wide ? stored : overlay;
  const setOpen = (v: boolean) => (wide ? setStored(v) : setOverlay(v));
  return { open, setOpen, toggle: () => setOpen(!open), wide };
}
